import classNames from 'classnames'
import deepEqual from 'fast-deep-equal'
import React, {memo, useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'
import {useHref, useLinkClickHandler} from 'react-router-dom'

import {useBuildStatusIconRelay} from '../../../../../../hooks/useBuildStatusIcon'
import JobIconStatus from '../../../components/common/JobIconStatus/JobIconStatus'
import type {pipelineHeadFragment$key} from '../../../hooks/__generated__/pipelineHeadFragment.graphql'
import {usePipelineId} from '../../../hooks/pipeline'
import {useIsJobDeleted, useLastRun} from '../../EditPipelinePage.hooks'
import {getJobName, hasJobError} from '../../EditPipelinePage.selectors'

import type {JobTileLastRunFragment$key} from './__generated__/JobTileLastRunFragment.graphql'
import JobAnimationWrapper from './JobAnimationWrapper/JobAnimationWrapper'

import styles from './JobTile.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  id: string
  selected: boolean
  isNew?: boolean
  x: number
  y: number
  pipelineHeadKey: pipelineHeadFragment$key | null
}

const lastRunFragment = graphql`
  fragment JobTileLastRunFragment on Build {
    ...useBuildStatusIconFragment
    parallelized
    status
  }
`

function JobTile({id, selected, isNew, x, y, pipelineHeadKey}: Props) {
  const to = useMemo(() => ({search: `job=${encodeURIComponent(id)}`}), [id])
  const href = useHref(to)
  const onClick = useLinkClickHandler(to)
  const pipelineId = usePipelineId()
  const name = useAppSelector(state => getJobName(state, pipelineId, id))
  const isEdited = useAppSelector(state => {
    if (name !== id) {
      return true
    }
    const {draft, original} = {...state.pipelines.pipelineDraft[pipelineId]}
    const draftJob = {...draft?.settings?.jobs?.[id]}
    const originalJob = original?.settings?.jobs?.[id]
    // Task: TCP-22 Rework "Use files..." link on Dependencies block
    // Hot fix: not detect changes in the Job file dependencies
    // Start
    const originalJobDependency = originalJob?.dependencies?.reduce<
      Record<string, Record<string, string[]>>
    >((accumulator, value) => {
      if (typeof value === 'object') {
        return {
          ...accumulator,
          ...value,
        }
      }
      return accumulator
    }, {})

    draftJob?.dependencies?.forEach((dependency, index) => {
      if (typeof dependency === 'object') {
        const [jobId] = Object.keys(dependency)
        const originalDependency = originalJobDependency?.[jobId]
        if (originalDependency && draftJob?.dependencies?.[index]) {
          const dependencies = [...draftJob.dependencies]

          dependencies[index] = {
            [jobId]: originalDependency,
          }

          draftJob.dependencies = dependencies
        }
      }
    })
    // End
    // Hot fix : not detect changes in the Job file dependencies

    return !deepEqual(draftJob, originalJob)
  })
  const isHoveredInSidebar = useAppSelector(state => state.pipelines.hoveredJob === id)

  const nameIsEmpty = name == null || name.length === 0

  const isDeleted = useIsJobDeleted(id)
  const runKey = useLastRun(id, pipelineHeadKey)
  const run = useFragment<JobTileLastRunFragment$key>(lastRunFragment, runKey)
  const hasError = useAppSelector(hasJobError(id))
  const statusIcon = useBuildStatusIconRelay(run)
  const icon = isDeleted ? 'trash' : run != null ? statusIcon : 'buildType-14px'

  return (
    <div className={styles.container} style={{top: y, left: x}}>
      <JobAnimationWrapper enabled={isNew === true}>
        <a
          title={`Edit job ${name}`}
          href={href}
          onClick={onClick}
          className={classNames(styles.job, {
            [styles.selected]: selected,
            [styles.hoveredInSidebar]: isHoveredInSidebar,
            [styles.error]: nameIsEmpty && !selected,
          })}
        >
          <span className={styles.top}>
            <JobIconStatus
              parallelized={run?.parallelized}
              status={run?.status}
              icon={icon}
              className={classNames({
                [styles.jobIcon]: run == null && !isDeleted,
                [styles.trashIcon]: isDeleted,
              })}
            />
            {isDeleted ? (
              <span className={styles.deletedLabel}>{'Deleted'}</span>
            ) : selected ? (
              <span className={styles.selectedLabel}>{'Editing'}</span>
            ) : isEdited ? (
              <span className={styles.editedLabel}>{isNew ? 'New' : 'Edited'}</span>
            ) : null}
          </span>
          <span className={classNames(styles.name, {[styles.deleted]: isDeleted})}>
            {nameIsEmpty ? (selected ? 'Enter the name' : 'Unnamed job') : name}
          </span>
          {hasError && <div className={styles.errorMessage}>{'Settings error'}</div>}
        </a>
      </JobAnimationWrapper>
    </div>
  )
}

export default memo<Props>(JobTile)
